import ConnectWalletButton from 'components/ConnectWalletButton'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import useSWR from 'swr'
import styled from 'styled-components'
import { ApiError, Person, Profile, api, errorText } from './api'
import { PnlCard, usePnlEnabled } from './PnlCard'
import { ActionBtn, Icon, IdentityCard, Note, SectionTitle, Surface, TabPanel, Tabs, useShare, useTab } from './Identity'
import { NftsCard } from './NftsCard'
import { ActivityCard, UnclaimedProfile } from './AutoProfile'
import { OnchainProfileCard } from './OnchainCard'
import { useSocialSession } from './useSocialSession'
import { Achievements, Muted, PersonItem } from './ui'

const Page = styled.main`
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
  padding: 16px 16px 120px;
  display: grid;
  gap: 14px;
  ${({ theme }) => theme.mediaQueries.md} {
    padding: 28px 24px 80px;
    gap: 16px;
  }
`

export function People({ wallet, which, onClose }: { wallet: string; which: 'followers' | 'following'; onClose?: () => void }) {
  const { data } = useSWR<{ items: Person[]; more: boolean }>(`people:${wallet}:${which}`, () => api(`profiles/${wallet}/${which}`))
  return (
    <Surface data-testid={`people-${which}`}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <SectionTitle as="h2">{which === 'followers' ? 'Followers' : 'Following'}</SectionTitle>
        {onClose && (
          <button type="button" onClick={onClose} aria-label="Close list" style={{ width: 36, height: 36, borderRadius: 10, border: 0, background: 'rgba(255,255,255,0.06)', color: 'inherit', cursor: 'pointer', display: 'grid', placeItems: 'center' }}>
            <Icon name="close" size={16} />
          </button>
        )}
      </div>
      <div style={{ marginTop: 8 }}>
        {!data && <Note>Loading…</Note>}
        {data && !data.items.length && <Note>Nobody yet.</Note>}
        {data?.items.map((p) => <PersonItem key={p.address} p={p} />)}
      </div>
    </Surface>
  )
}

export default function PublicProfile({ id }: { id: string }) {
  const { session, address, signIn, busy, error: signError } = useSocialSession()
  const token = session?.token
  const { data: p, error, mutate } = useSWR<Profile>(id ? `profile:${id}:${token ? 'a' : 'n'}` : null, () => api<Profile>(`profiles/${id}`, { token }), { revalidateOnFocus: false })
  const [list, setList] = useState<'followers' | 'following' | null>(null)
  const [nftCount, setNftCount] = useState<number | undefined>()
  const [tab, setTab] = useTab()
  const pnlOn = usePnlEnabled()
  const [msg, setMsg] = useState<string | null>(null)
  const url = typeof window !== 'undefined' ? window.location.href : ''
  const { share, done: shared } = useShare(url, p?.handle ? `@${p.handle} on CADINU` : 'CADINU profile')
  // an old /profile/<address>[/achievements|/referral] link (next.config redirect, ?from=profile): your own wallet opens your editable /u
  const router = useRouter()
  useEffect(() => {
    if (router.query.from !== 'profile' || !address || !id) return
    if (id.toLowerCase() === address.toLowerCase()) router.replace(`/u${window.location.hash}`)
  }, [router, address, id])

  const toggleFollow = async () => {
    if (!p) return
    setMsg(null)
    const t = token ?? (await signIn())?.token
    if (!t) return
    try {
      const r = await api<{ following: boolean; followers: number }>(`profiles/${p.address}/follow`, { method: p.viewer_follows ? 'DELETE' : 'POST', token: t })
      mutate({ ...p, viewer_follows: r.following, followers: r.followers }, false)
    } catch (e) {
      setMsg(errorText(e))
    }
  }

  // 2026-10-07: an address that never signed in has its automatic page (public activity, marked unclaimed, noindex); a handle that does not
  // exist stays "not found" (handles belong to claimed profiles only)
  const isAddr = /^0x[0-9a-fA-F]{40}$/.test(id)
  const unclaimed = (error instanceof ApiError && error.status === 404) || p?.state === 'none'
  if (unclaimed && isAddr) {
    const own = Boolean(address && address.toLowerCase() === id.toLowerCase())
    return (
      <Page>
        <UnclaimedProfile wallet={id} mine={own} onClaim={signIn} busy={busy} error={signError} />
        <OnchainProfileCard wallet={id as `0x${string}`} mine={false} />
      </Page>
    )
  }
  if (error || unclaimed)
    return (
      <Page>
        <Surface $lit>
          <SectionTitle as="h1">{unclaimed ? 'No profile with this name' : 'Profile unavailable'}</SectionTitle>
          <Note style={{ marginTop: 8 }}>{unclaimed ? 'No CADINU profile uses this handle. Profiles can also be opened by their wallet address.' : 'The profile could not be loaded. Try again in a moment.'}</Note>
        </Surface>
      </Page>
    )
  if (!p)
    return (
      <Page>
        <Surface style={{ minHeight: 220 }}>
          <Note>Loading profile…</Note>
        </Surface>
      </Page>
    )
  if (p.state === 'off')
    return (
      <Page>
        <IdentityCard p={p}>
          <Note style={{ marginTop: 14 }}>The owner of this wallet has switched their profile off. Only the address is shown.</Note>
        </IdentityCard>
      </Page>
    )
  const mine = Boolean(p.is_me) || (address && address.toLowerCase() === p.address.toLowerCase())
  const earned = p.achievements?.filter((a) => a.earned).length
  return (
    <Page>
      <IdentityCard
        p={p}
        onCounts={(w) => setList(list === w ? null : w)}
        openList={list}
        actions={
          <>
            {mine ? (
              <ActionBtn as="a" href="/u" $primary data-testid="edit-btn">
                <Icon name="edit" size={18} />
                Edit
              </ActionBtn>
            ) : !address ? (
              <ConnectWalletButton scale="md" data-testid="follow-btn">
                Connect to follow
              </ConnectWalletButton>
            ) : (
              <ActionBtn type="button" onClick={toggleFollow} disabled={busy} $primary={!p.viewer_follows} data-testid="follow-btn">
                {p.viewer_follows ? <Icon name="check" size={18} /> : <Icon name="users" size={18} />}
                {p.viewer_follows ? 'Following' : 'Follow'}
              </ActionBtn>
            )}
            <ActionBtn type="button" onClick={share} data-testid="share-btn" aria-label="Share profile">
              <Icon name={shared ? 'check' : 'share'} size={18} />
              {shared ? 'Link copied' : 'Share'}
            </ActionBtn>
          </>
        }
      >
        {msg && (
          <Muted role="alert" style={{ marginTop: 8 }}>
            {msg}
          </Muted>
        )}
      </IdentityCard>
      {list && <People wallet={p.address} which={list} onClose={() => setList(null)} />}
      <Tabs tab={tab} setTab={setTab} counts={{ achievements: earned, nfts: nftCount }} publicView />
      <TabPanel id="achievements" tab={tab}>
        {p.achievements && <Achievements items={p.achievements} />}
      </TabPanel>
      <TabPanel id="nfts" tab={tab}>
        <NftsCard wallet={p.address} onCount={setNftCount} />
      </TabPanel>
      <TabPanel id="rewards" tab={tab}>
        <OnchainProfileCard wallet={p.address as `0x${string}`} mine={false} />
        {!pnlOn ? null : p.show_pnl ? (
          <PnlCard wallet={p.address} />
        ) : (
          <Surface>
            <SectionTitle as="h2">Profit</SectionTitle>
            <Note style={{ marginTop: 8 }}>{mine ? 'Hidden. Turn on "Show profit" in Edit → Privacy.' : 'This user keeps profit private.'}</Note>
          </Surface>
        )}
      </TabPanel>
      <ActivityCard wallet={p.address} mine={Boolean(mine)} />
    </Page>
  )
}
